import { Link } from "react-router-dom";
import type { Topic } from "@/types/content";
import AttitudeBar from "@/components/base/AttitudeBar";
import { formatNumber } from "@/utils/format";

interface TopicAsideProps {
  topic: Topic;
  related: Topic[];
}

export default function TopicAside({ topic, related }: TopicAsideProps) {
  const info = [
    { label: "整理", value: topic.author, icon: "ri-edit-line" },
    { label: "更新", value: topic.date, icon: "ri-calendar-line" },
    { label: "热度", value: formatNumber(topic.heat), icon: "ri-fire-line" },
    { label: "讨论", value: `${formatNumber(topic.commentCount)} 条`, icon: "ri-chat-3-line" },
    { label: "关注", value: formatNumber(topic.followers), icon: "ri-user-heart-line" },
    { label: "阅读", value: topic.readTime, icon: "ri-time-line" },
  ];

  return (
    <aside className="space-y-5 lg:sticky lg:top-24">
      <section
        aria-label="舆论态度分布"
        className="rounded-lg border border-background-200 bg-background-50 p-5"
      >
        <div className="flex items-center gap-2">
          <span className="flex h-4 w-4 items-center justify-center text-accent-600">
            <i className="ri-pie-chart-2-line" aria-hidden="true" />
          </span>
          <h2 className="font-heading text-base font-bold text-foreground-950">
            舆论态度分布
          </h2>
        </div>
        <p className="mt-2 text-xs leading-relaxed text-foreground-500">
          根据本话题投票结果折算，参与人数 {formatNumber(topic.poll.totalVotes)} 人。
        </p>
        <div className="mt-4">
          <AttitudeBar attitude={topic.attitude} />
        </div>
      </section>

      <section
        aria-label="话题信息"
        className="rounded-lg border border-background-200 bg-background-50 p-5"
      >
        <h2 className="font-heading text-base font-bold text-foreground-950">话题信息</h2>
        <dl className="mt-4 space-y-3">
          {info.map((item) => (
            <div key={item.label} className="flex items-center justify-between gap-3">
              <dt className="inline-flex items-center gap-2 font-label text-xs text-foreground-500">
                <span className="flex h-4 w-4 items-center justify-center text-secondary-600">
                  <i className={item.icon} aria-hidden="true" />
                </span>
                {item.label}
              </dt>
              <dd className="font-label text-xs font-medium text-foreground-900">{item.value}</dd>
            </div>
          ))}
        </dl>
        <div className="mt-4 flex flex-wrap gap-2 border-t border-background-200 pt-4">
          {topic.tags.map((tag) => (
            <span
              key={tag}
              className="rounded-full bg-secondary-50 px-2.5 py-1 font-label text-xs text-secondary-800"
            >
              #{tag}
            </span>
          ))}
        </div>
      </section>

      <section
        aria-label="相关推荐"
        className="rounded-lg border border-background-200 bg-background-50 p-5"
      >
        <h2 className="font-heading text-base font-bold text-foreground-950">相关推荐</h2>
        <ol className="mt-4 space-y-3">
          {related.map((item, index) => (
            <li key={item.id} className="flex gap-3">
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-background-200 font-heading text-xs font-bold text-foreground-600">
                {index + 1}
              </span>
              <span className="min-w-0">
                <Link
                  to={`/topics/${item.id}`}
                  className="line-clamp-2 cursor-pointer font-label text-sm leading-snug text-foreground-800 transition-colors hover:text-primary-600"
                >
                  {item.title}
                </Link>
                <span className="mt-1 block font-label text-xs text-foreground-400">
                  {item.category} · {formatNumber(item.heat)} 热度
                </span>
              </span>
            </li>
          ))}
        </ol>
        <Link
          to="/topics"
          className="mt-4 inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap font-label text-xs text-secondary-700 transition-colors hover:text-primary-600"
        >
          返回话题广场
          <span className="flex h-3.5 w-3.5 items-center justify-center">
            <i className="ri-arrow-right-line" aria-hidden="true" />
          </span>
        </Link>
      </section>
    </aside>
  );
}